import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [role, setRole] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [timezone, setTimezone] = useState("America/New_York");
  const [pageLoading, setPageLoading] = useState(true);
  const [name, setName] = useState(null);
  const [email, setEmail] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    isAuthenticated: false,
    token: null,
    creditBalance: 0,
    role: null,
    onboarding: false,
    timezone: "America/New_York",
    name: null,
    email: null,
    userId: null,
    discordUsername: null,
    telegramUsername: null,
    subscribed: false,
  });

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedCreditBalance = localStorage.getItem("creditBalance");
    const storedRole = localStorage.getItem("userRole");
    const storedOnboarding = localStorage.getItem("onboarding");
    const storedTimezone = localStorage.getItem("timezone");
    const storedName = localStorage.getItem("name");
    const storedEmail = localStorage.getItem("email");
    const storedSubscribed = localStorage.getItem("subscribed");

    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
    }

    if (storedCreditBalance) {
      setCreditBalance(Number(storedCreditBalance));
    }

    if (storedRole) {
      setRole(storedRole);
    }

    if (storedOnboarding) {
      setOnboarding(storedOnboarding === "true");
    }

    if (storedTimezone) {
      setTimezone(storedTimezone);
    }

    if (storedName) {
      setName(storedName);
    }

    if (storedEmail) {
      setEmail(storedEmail);
    }

    if (storedSubscribed) {
      setSubscribed(storedSubscribed === "true");
    }

    setPageLoading(false);
  }, []);

  useEffect(() => {
    const storedUserData = localStorage.getItem("userData");

    if (storedUserData) {
      const parsedData = JSON.parse(storedUserData);
      setUserData({
        ...parsedData,
        isAuthenticated: !!parsedData.token,
      });
    }

    setPageLoading(false);
  }, []);

  const login = (data) => {
    setToken(data.token);
    setIsAuthenticated(true);
    setCreditBalance(data.creditBalance);
    setRole(data.role);
    setOnboarding(data.onboarding || false);
    setTimezone(data.timezone || "America/New_York");
    setName(data.name || null);
    setEmail(data.email || null);
    setSubscribed(data.subscribed || false);

    localStorage.setItem("token", data.token);
    localStorage.setItem("name", data.name || "");
    localStorage.setItem("email", data.email || "");
    localStorage.setItem("userId", data.userId);
    localStorage.setItem("userRole", data.role);
    localStorage.setItem("creditBalance", data.creditBalance);
    localStorage.setItem("onboarding", data.onboarding || false);
    localStorage.setItem("timezone", data.timezone || "America/New_York");
    localStorage.setItem("subscribed", data.subscribed || false);
    localStorage.setItem(
      "eachTimeLoginMessage",
      data.eachTimeLoginMessage || "Great to see you again!"
    );
    localStorage.setItem(
      "firstTimeLoginMessage",
      data.firstTimeLoginMessage || "Welcome to SportsGPT!"
    );

    const newUserData = {
      isAuthenticated: true,
      token: data.token,
      creditBalance: data.creditBalance,
      role: data.role,
      onboarding: data.onboarding || false,
      timezone: data.timezone || "America/New_York",
      name: data.name || null,
      email: data.email || null,
      userId: data.userId || null,
      discordUsername: data.discordUsername || null,
      telegramUsername: data.telegramUsername || null,
      freeAutoSchedule: data.freeAutoSchedule,
      subscribed: data.subscribed || false,
    };

    setUserData(newUserData);
    localStorage.setItem("userData", JSON.stringify(newUserData));
  };

  const logout = () => {
    setToken(null);
    setIsAuthenticated(false);
    setCreditBalance(0);
    setRole(null);
    setOnboarding(false);
    setTimezone("America/New_York");
    setName(null);
    setEmail(null);
    setSubscribed(false);

    localStorage.removeItem("token");
    localStorage.removeItem("name");
    localStorage.removeItem("email");
    localStorage.removeItem("userId");
    localStorage.removeItem("userRole");
    localStorage.removeItem("creditBalance");
    localStorage.removeItem("onboarding");
    localStorage.removeItem("timezone");
    localStorage.removeItem("subscribed");
    localStorage.removeItem("eachTimeLoginMessage");
    localStorage.removeItem("firstTimeLoginMessage");

    setUserData({
      isAuthenticated: false,
      token: null,
      creditBalance: 0,
      role: null,
      onboarding: false,
      timezone: "America/New_York",
      name: null,
      email: null,
      userId: null,
      discordUsername: null,
      telegramUsername: null,
      subscribed: false,
    });

    localStorage.removeItem("userData");
    navigate("/login");
  };

  const updateCreditBalance = (newBalance) => {
    setCreditBalance(newBalance);
    localStorage.setItem("creditBalance", newBalance);

    const updatedData = { ...userData, creditBalance: newBalance };
    setUserData(updatedData);
    localStorage.setItem("userData", JSON.stringify(updatedData));
  };

  const updateUserDetails = (newName, newTimezone) => {
    setName(newName);
    setTimezone(newTimezone);
    localStorage.setItem("name", newName);
    localStorage.setItem("timezone", newTimezone);

    const updatedData = { ...userData, name: newName, timezone: newTimezone };
    setUserData(updatedData);
    localStorage.setItem("userData", JSON.stringify(updatedData));
  };

  const isAdmin = () => role === "admin";

  return (
    <AuthContext.Provider
      value={{
        userData,
        setUserData,
        isAuthenticated,
        token,
        isAdmin,
        role,
        creditBalance,
        onboarding,
        timezone,
        name,
        email,
        subscribed,
        updateCreditBalance,
        updateUserDetails,
        login,
        logout,
        pageLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};